import { AfterViewInit, Component, OnDestroy, TrackByFunction } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { combineLatest, Observable, of, Subject } from 'rxjs'
import { map, shareReplay, switchMap, tap } from 'rxjs/operators'
import { RankingsService } from '../rankings.service'
import { NodeService } from '../../shared/services/node.service'
import { AuthService } from '../../singleton-services/auth/auth.service'
import { INode } from '../../shared/models/node.model'
import { AdComponentInputs } from '../../shared/ads/ad/ad-component.interface'
import { AdConfig, AdTargeting, AdTypes } from '../../shared/ads/advertising-service/ad-config'
import {
  AD_ADHESION_SIZE,
  AD_LEADERBOARD_LARGE_FLEX_SIZE,
  AD_LEADERBOARD_SMALL_SIZE,
  AD_RECTANGLE_LARGE_SIZE,
  AD_RECTANGLE_SMALL_SIZE,
  AD_SKIN_SIZE
} from '../../shared/ads/ads.utility'
import { environment } from '../../../environments/environment'
import { ContentItem } from '../../libs/primary-related-content/primary-related-content.component'
import { RelatedContentService } from '../../libs/primary-related-content/related-content.service'
import { buildAuthorDate, buildAuthorLeadInText } from '../../shared/author-avatar/author-date/author-date.utility'
import { IRankingForView } from '../interfaces/ranking-for-view.interface'
import { IRankingSection } from '../interfaces/ranking-section.interface'
import { ResourceType } from 'src/app/singleton-services/auth/auth.interfaces'
import { getIdFromSlug } from 'src/app/shared/utility-functions/slug.utility'

@Component({
  selector: 'flo-ranking',
  templateUrl: './ranking.component.html',
  styleUrls: ['./ranking.component.scss']
})
export class RankingComponent implements AfterViewInit, OnDestroy {
  private readonly ngOnDestroy$ = new Subject<void>()
  private readonly ngAfterViewInit$ = new Subject<void>()
  private readonly slug$ = this.route.params.pipe(map((r: any) => r.rankingSlug))
  private readonly rankingSetId$ = this.slug$.pipe(map(slug => getIdFromSlug(slug)))
  private readonly ranking$ = this.rankingsService.ranking$.pipe(
    map(a => a.data as IRankingForView),
    shareReplay(1)
  )

  public filterForm = new UntypedFormGroup({
    sort: new UntypedFormControl()
  })

  private readonly targeting$: Observable<AdTargeting> = this.ranking$.pipe(
    map(r => ({
      contentIds: r.aggregated_node_ids,
      categorySlugs: r.aggregated_category_slugs,
      analyticsTracking: r
    }))
  )
  private readonly adConfigMap = new Map<AdTypes, AdComponentInputs>()
  public readonly AdComponent = import('../../shared/ads/ad/ad.component').then(m => m.AdComponent)

  public readonly adConfigs$: Observable<Map<AdTypes, AdComponentInputs>> = this.targeting$.pipe(
    map(targeting => {
      this.adConfigMap.set('skin', {
        config: {
          adConfig: AdConfig('rankings/post/atf', AD_SKIN_SIZE, 'skin-ranking-post', targeting)
        }
      })

      this.adConfigMap.set('leaderboard-large', {
        config: {
          adConfig: AdConfig(
            'rankings/post/728x90_flex',
            AD_LEADERBOARD_LARGE_FLEX_SIZE,
            'leaderboard-large-ranking-post',
            targeting
          ),
          additionalOpts: { deferClick: environment.advertising.preventClickTimeout }
        }
      })

      this.adConfigMap.set('leaderboard-small', {
        config: {
          adConfig: AdConfig(
            'rankings/post/728x90_flex',
            AD_LEADERBOARD_SMALL_SIZE,
            'leaderboard-small-ranking-post',
            targeting
          )
        }
      })

      this.adConfigMap.set('rectangle-small', {
        config: {
          adConfig: AdConfig(
            'rankings/post/300x250_1',
            AD_RECTANGLE_SMALL_SIZE,
            'rectangle-right-rail-ranking-post',
            targeting
          )
        }
      })

      this.adConfigMap.set('rectangle-large-sticky', {
        config: {
          adConfig: AdConfig('rankings/post/300x250_2', AD_RECTANGLE_LARGE_SIZE, 'news-ad-second-rectangle', targeting),
          additionalOpts: { maxRefreshTime: 60000 }
        }
      })

      this.adConfigMap.set('adhesion', {
        config: {
          adConfig: AdConfig('rankings/post/adhesion', AD_ADHESION_SIZE, 'adhesion-ranking-post', targeting),
          additionalOpts: { maxRefreshTime: 30000 }
        }
      })

      this.adConfigMap.set('content-mobile', {
        config: {
          adConfig: AdConfig('rankings/post/300x250_1', AD_ADHESION_SIZE, 'mobile-content-ranking-post', targeting)
        }
      })

      return this.adConfigMap
    })
  )

  private user$ = this.authService.user$.pipe(shareReplay(1))

  /**
   * Is a user entitled to this content
   * This is behind the feature flag
   */
  private hasAccess$ = this.ranking$.pipe(
    switchMap(ranking => {
      return this.authService.hasAccess(ranking, ResourceType.Node)
    })
  )

  public view$ = combineLatest([this.ranking$, this.rankingSetId$, this.user$, this.hasAccess$]).pipe(
    map(([ranking, rankingSetId, user, hasAccess]) => {
      let tabList: ReadonlyArray<any> = []
      Object.keys(ranking.ranking_sections).forEach(k => {
        const currentArray = ranking.ranking_sections[k]
        let tabAdded = false
        currentArray.map((section: IRankingSection, i: number) => {
          if (section.id === rankingSetId) {
            const metaProps = {
              title: section.name,
              asset: section.asset,
              seo_description: section.seo_description
            } as INode
            ranking.filterList = currentArray
            ranking.currentRankingSet = section
            tabList = [...tabList, { name: section.name, uri: section.slug_uri, active: true }]
            this.nodeService.applyNodeMetaToPage(metaProps)
            tabAdded = true
          }

          // grab last item
          if (currentArray.length === i + 1 && !tabAdded) {
            return (tabList = [...tabList, { name: section.name, uri: section.slug_uri }])
          }

          return undefined
        })

        ranking.tabList = tabList
        return
      })

      return {
        ...ranking,
        authorDate: buildAuthorDate(ranking.author, ranking.author.modified_at || ranking.publish_start_date),
        authorLeadInText: buildAuthorLeadInText(
          ranking.author,
          ranking.author.modified_at || ranking.publish_start_date
        ),
        assocId:
          ranking.node &&
          ranking.node.primary_event_or_series_association &&
          ranking.node.primary_event_or_series_association.id,
        paywall: !hasAccess,
        loggedIn: user.loggedIn,
        redirectParam: ranking.latest_ranking_slug_uri,
        filterList: ranking.filterList?.map((dropdown: any) => {
          return {
            ...dropdown,
            selected: dropdown.id === ranking.currentRankingSet.id ? true : undefined
          }
        })
      }
    })
  )

  public paywallMessage = 'Unlock these rankings, live events, and more with a subscription!'

  public rightRailRelatedContentTitle = `More Rankings`

  /**
   * Right rail content turned off as part of FloCollege launch {@link https://flocasts.atlassian.net/browse/CXP-8121}
   * Refer to {@link https://flocasts.atlassian.net/browse/CXP-8122} to restore.
   */
  public readonly rightRailRelatedContent$: Observable<ReadonlyArray<ContentItem>> = this.ranking$.pipe(
    map(r => r.id),
    switchMap(id => of([]))
  )
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly rankingsService: RankingsService,
    private readonly nodeService: NodeService,
    private readonly authService: AuthService,
    private readonly relatedContent: RelatedContentService
  ) {}

  public onNavigate(location: any) {
    this.router.navigate([`../${location}`])
  }

  trackByFilter: TrackByFunction<any> = (index: number, item: any) => {
    return item.id
  }

  trackByTab: TrackByFunction<any> = (index: number, item: any) => {
    return item.name
  }

  public ngAfterViewInit(): void {
    this.ngAfterViewInit$.next()
    this.ngAfterViewInit$.complete()
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
